/**
 * Client-side helpers for the test-inbox message endpoints. Inlined
 * rather than depending on `@enduring-systems/test-inbox-client`
 * because the published 0.2.x SDK predates the list/detail/raw routes;
 * once the SDK republishes the page can switch over wholesale.
 */

export interface EmailAddress {
  email: string;
  name: string | null;
}

export interface MessageEnvelope {
  spf?: string;
  dkim?: string;
  dmarc?: string;
  dmarcPolicy?: string;
}

export interface MessageSummary {
  id: string;
  namespaceId: string;
  sesMessageId: string;
  subject: string | null;
  from: EmailAddress[];
  to: EmailAddress[];
  receivedAt: string;
  envelope: MessageEnvelope;
}

export interface Message extends MessageSummary {
  rawMimeObjectKey: string;
  text?: {
    body: string;
    links: string[];
    codes: string[];
  };
  html?: {
    body: string;
    links: string[];
    codes: string[];
  };
  attachments: {
    id: string;
    fileName: string | null;
    contentType: string | null;
    length: number | null;
  }[];
}

export interface MessageList {
  items: MessageSummary[];
  nextCursor: string | null;
}

export interface ListMessagesOptions {
  cursor?: string;
  limit?: number;
}

export async function fetchMessages(
  baseUrl: string,
  namespaceId: string,
  opts: ListMessagesOptions = {}
): Promise<MessageList> {
  const params = new URLSearchParams();
  if (opts.cursor) params.set("cursor", opts.cursor);
  if (opts.limit !== undefined) params.set("limit", String(opts.limit));

  const qs = params.toString();
  const path = `/namespaces/${namespaceId}/messages${qs.length > 0 ? `?${qs}` : ""}`;
  const res = await fetch(`${baseUrl}${path}`);
  if (!res.ok) {
    throw new Error(`fetchMessages failed with status ${res.status}`);
  }
  return (await res.json()) as MessageList;
}

export type FetchMessageResult =
  | { status: "ok"; message: Message }
  | { status: "not-found" }
  | { status: "forbidden" };

export async function fetchMessage(
  baseUrl: string,
  namespaceId: string,
  messageId: string
): Promise<FetchMessageResult> {
  const res = await fetch(`${baseUrl}/namespaces/${namespaceId}/messages/${messageId}`);
  if (res.ok) {
    return { status: "ok", message: (await res.json()) as Message };
  }
  if (res.status === 408) return { status: "not-found" };
  if (res.status === 403) return { status: "forbidden" };
  throw new Error(`fetchMessage failed with status ${res.status}`);
}

export type FetchRawMimeResult =
  | { status: "ok"; body: string }
  | { status: "not-found" }
  | { status: "forbidden" };

export async function fetchRawMime(
  baseUrl: string,
  namespaceId: string,
  messageId: string
): Promise<FetchRawMimeResult> {
  const res = await fetch(`${baseUrl}/namespaces/${namespaceId}/messages/${messageId}/raw`);
  if (res.ok) {
    return { status: "ok", body: await res.text() };
  }
  if (res.status === 408) return { status: "not-found" };
  if (res.status === 403) return { status: "forbidden" };
  throw new Error(`fetchRawMime failed with status ${res.status}`);
}

export interface SearchMessagesOptions {
  fromSender?: string;
  subjectContains?: string;
  /** ISO datetime — start of the time range. */
  from?: string;
  /** ISO datetime — end of the time range. */
  to?: string;
  limit?: number;
  cursor?: string;
}

export interface MessageSearchResult {
  items: MessageSummary[];
  nextCursor: string | null;
}

/**
 * Search messages across all namespaces in the env.
 *
 * The server requires at least one of fromSender / subjectContains / from /
 * to — passing an empty options object will throw with status 400. Callers
 * should guard before calling (the hook and the page both do this).
 *
 * Time-range semantics (enforced server-side):
 *   - Only `from`: implied window [from, now], max 30 days.
 *   - Only `to`: implied window [to − 30d, to].
 *   - Both: must be non-inverted and ≤ 30 days.
 */
export async function searchMessages(
  baseUrl: string,
  opts: SearchMessagesOptions
): Promise<MessageSearchResult> {
  const params = new URLSearchParams();
  if (opts.fromSender) params.set("fromSender", opts.fromSender);
  if (opts.subjectContains) params.set("subjectContains", opts.subjectContains);
  if (opts.from) params.set("from", opts.from);
  if (opts.to) params.set("to", opts.to);
  if (opts.limit !== undefined) params.set("limit", String(opts.limit));
  if (opts.cursor) params.set("cursor", opts.cursor);

  const res = await fetch(`${baseUrl}/messages/search?${params.toString()}`);
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    const msg = body?.error?.message ?? `searchMessages failed with status ${res.status}`;
    throw new Error(msg);
  }
  return (await res.json()) as MessageSearchResult;
}
